import React from "react";
import {
  createIndicator,
  indicatorRegistry,
} from "../registry/IndicatorRegistry";
import {
  VectorCandlesIndicator,
  VectorCandlesProps,
} from "../../components/indicators/VectorCandlesIndicator";
import {
  PlasmaLinesIndicator,
  PlasmaLinesProps,
} from "../../components/indicators/PlasmaLines";
import {
  BreakoutSignalsIndicator,
  BreakoutSignalsProps,
} from "../../components/indicators/BreakoutSignals";
import {
  SupportResistanceIndicator,
  SupportResistanceProps,
} from "../../components/indicators/SupportResistance";
import {
  TcTrendIndicator,
  TcTrendProps,
} from "../../components/indicators/TcTrendIndicator";
import {
  DeltaTrendIndicator,
  DeltaTrendProps,
} from "../../components/indicators/DeltaTrendIndicator";
import {
  FearGreedIndicator,
  FearGreedIndicatorProps,
} from "../../components/indicators/FearGreedIndicator";
import {
  AuctionMarketIndicator,
  AuctionMarketIndicatorProps,
} from "../../components/indicators/AuctionMarketIndicator";

let hasRegistered = false;

/**
 * Register all available indicators
 *
 * Each indicator is self-managed and handles its own series creation/cleanup
 * based on the isEnabled prop. This ensures proper show/hide behavior without
 * overlapping series or memory leaks.
 *
 * This should be called once when the application starts
 */
export function registerIndicators() {
  if (hasRegistered) {
    return;
  }
  hasRegistered = true;
  // Avoid TSX generic parsing ambiguity by aliasing the generic factory
  const ci = createIndicator;
  // Vector Candles Indicator
  indicatorRegistry.register(
    ci(
      {
        id: "vectorcandles",
        name: "Vector Candles",
        defaultEnabled: false,
        category: "signals",
        description:
          "Shows buy/sell signal markers based on vector candle analysis",
      },
      (props: VectorCandlesProps) => <VectorCandlesIndicator {...props} />
    )
  );

  // Plasma Lines Indicator
  indicatorRegistry.register(
    ci(
      {
        id: "plasma",
        name: "Plasma",
        defaultEnabled: false,
        category: "trend",
        description: "Shows plasma trend lines for market direction analysis",
      },
      (props: PlasmaLinesProps) => <PlasmaLinesIndicator {...props} />
    )
  );

  // Breakout Signals Indicator
  indicatorRegistry.register(
    ci(
      {
        id: "breakout",
        name: "Breakout Carver",
        defaultEnabled: false,
        category: "signals",
        description: "Shows breakout signals for long and short positions",
      },
      (props: BreakoutSignalsProps) => <BreakoutSignalsIndicator {...props} />
    )
  );

  // Fear Carver indicator (Breakout signals gated by sentiment)
  indicatorRegistry.register(
    ci(
      {
        id: "fearcarver",
        name: "Fear Carver",
        defaultEnabled: false,
        category: "signals",
        description:
          "Breakout plays only when sentiment >60 longs / <40 shorts",
      },
      (props: BreakoutSignalsProps) => (
        <BreakoutSignalsIndicator
          {...props}
          longColor="#facc15"
          shortColor="#f97316"
          longLabel="FC+"
          shortLabel="FC-"
        />
      )
    )
  );

  // TcBreakout indicator - combines Breakout with TcTrend direction
  indicatorRegistry.register(
    ci(
      {
        id: "tcbreakout",
        name: "TC Breakout",
        defaultEnabled: false,
        category: "signals",
        description: "Breakouts allowed only when TC trend aligns",
      },
      (props: BreakoutSignalsProps) => (
        <BreakoutSignalsIndicator
          {...props}
          longColor="#22c55e"
          shortColor="#ef4444"
          longLabel="BUY"
          shortLabel="SELL"
        />
      )
    )
  );

  // Support/Resistance Indicator
  indicatorRegistry.register(
    ci(
      {
        id: "supportresistance",
        name: "Support/Resistance",
        defaultEnabled: false,
        category: "levels",
        description: "Shows support and resistance level lines on the chart",
      },
      (props: SupportResistanceProps) => (
        <SupportResistanceIndicator {...props} />
      )
    )
  );

  // TcTrend Indicator (placeholder)
  indicatorRegistry.register(
    ci(
      {
        id: "tctrend",
        name: "TcTrend",
        defaultEnabled: false,
        category: "trend",
        description: "Technical trend analysis indicator (coming soon)",
      },
      (props: TcTrendProps) => <TcTrendIndicator {...props} />
    )
  );

  // DeltaTrend Indicator (line + dots + labels)
  indicatorRegistry.register(
    ci(
      {
        id: "deltatrend",
        name: "DeltaTrend",
        defaultEnabled: false,
        category: "trend",
        description: "DeltaLine + DeltaDots + Top/Bottom labels",
      },
      (props: DeltaTrendProps) => <DeltaTrendIndicator {...props} />
    )
  );

  // Fear & Greed sentiment overlay
  indicatorRegistry.register(
    ci(
      {
        id: "feargreed",
        name: "Fear & Greed",
        defaultEnabled: false,
        category: "sentiment",
        description: "Daily fear & greed index overlain on price",
      },
      (props: FearGreedIndicatorProps) => <FearGreedIndicator {...props} />
    )
  );

  // Auction Market Theory indicator
  indicatorRegistry.register(
    ci(
      {
        id: "auctionmarket",
        name: "Auction Market",
        defaultEnabled: false,
        category: "signals",
        description:
          "Signals based on liquidity sweeps, stop runs, and value area analysis",
      },
      (props: AuctionMarketIndicatorProps) => (
        <AuctionMarketIndicator {...props} />
      )
    )
  );
}

/**
 * Get all registered indicator configurations
 */
export function getIndicatorConfigs() {
  return indicatorRegistry.getConfigs();
}

/**
 * Get indicator definition by ID
 */
export function getIndicator(id: string) {
  return indicatorRegistry.get(id);
}
